import AdminLayout from '../components/AdminLayout'
import { AdminTable, Column } from '../components/AdminTable'
import { Filter } from 'lucide-react'
import AdminDateRangePicker, { IsoRange } from '../components/AdminDateRangePicker'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

type ReportRow = {
  key: string
  id: string
  type: string
  taxpayer: string
  date: string
  service: string
  method: string
  status: 'Pending' | 'Failed'
  // Fields needed to resume the filing in the customer wizard.
  filingId: string
  nino: string
  taxYear: string
}

export default function AdminReportsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<ReportRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [range, setRange] = useState<IsoRange>({})
  const itemsPerPage = 10
  const rangeStart = range.start
  const rangeEnd = range.end

  useEffect(() => {
    setCurrentPage(1)
  }, [rangeStart, rangeEnd])

  useEffect(() => {
    const loadData = async () => {
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.listFilings({
          page: currentPage,
          limit: itemsPerPage,
          notSubmitted: true,
          ...(rangeStart ? { start: rangeStart } : {}),
          ...(rangeEnd ? { end: rangeEnd } : {}),
        })
        const data: any = (response as any)?.data || response
        const items = data?.items || []
        setRows(
          items.map((item: any, idx: number) => {
            const id = item.id || item._id || `${idx}`
            const rawStatus = (item.status || '').toString().toLowerCase()
            return {
              key: id,
              id,
              type: item.type || 'CT600',
              taxpayer: item.taxpayer || '—',
              date: item.filingDate
                ? new Date(item.filingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—',
              service: item.type || '—',
              method: item.channel || 'Portal',
              // draft / completed / in-progress read as "Pending"; a bounced
              // submission (rejected) reads as "Failed".
              status: rawStatus === 'rejected' ? 'Failed' : 'Pending',
              filingId: id,
              nino: item.type === 'SelfAssessment' ? item.taxpayer || '' : '',
              taxYear: item.period || '',
            }
          })
        )

        const summary = data?.summary || {}
        setTotalItems(summary.total ?? data?.total ?? items.length ?? 0)
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load draft filings.')
      } finally {
        setLoading(false)
      }
    }

    void loadData()
  }, [currentPage, rangeStart, rangeEnd])

  const continueFiling = (row: ReportRow) => {
    if (row.type === 'SelfAssessment') {
      const params = new URLSearchParams({ flow: 'sa', fromHistory: 'true' })
      if (row.nino) params.set('nino', row.nino)
      if (row.taxYear) params.set('taxYear', row.taxYear)
      navigate(`/self-assessment/filing?${params.toString()}`)
      return
    }
    navigate(`/ct600-filing/${row.filingId}`)
  }

  const columns: Column<ReportRow>[] = [
    { key: 'id', label: 'ID' },
    { key: 'date', label: 'Created' },
    { key: 'service', label: 'Service' },
    { key: 'taxpayer', label: 'Taxpayer' },
    { key: 'method', label: 'Method' },
    {
      key: 'status',
      label: 'Status',
      render: row => {
        const style =
          row.status === 'Pending'
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-red-50 text-red-700 border-red-100'
        return (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style}`}>
            {row.status}
          </span>
        )
      },
    },
    {
      key: 'actions',
      label: '',
      render: row => (
        <button
          onClick={() => continueFiling(row)}
          className='inline-flex items-center gap-1 rounded-full bg-[#0b1f3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0a1a31]'
        >
          Continue filing
        </button>
      ),
    },
  ]

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Review Request</h1>
            <p className='text-sm text-gray-500'>
              Filings still in draft — open one to complete and submit it on the customer’s behalf.
            </p>
          </div>
          <div className='self-start sm:self-auto'>
            <AdminDateRangePicker onChange={setRange} />
          </div>
        </div>

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        <AdminTable<ReportRow>
          columns={columns}
          data={rows}
          loading={loading}
          emptyMessage='No draft filings awaiting review.'
          searchPlaceholder='Search draft filings'
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          actions={
            <button className='inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50'>
              <Filter className='h-4 w-4 text-gray-500' />
              Filter
            </button>
          }
        />
      </div>
    </AdminLayout>
  )
}
